import { useCallback, useEffect, useState } from 'react'
import api, { apiErrorMessage } from './client'
import type { Project, ProjectShare, Simulation } from './types'

interface Query<T> {
  data: T | undefined
  loading: boolean
  error: string
  reload: () => void
}

function useGet<T>(url: string): Query<T> {
  const [data, setData] = useState<T>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [tick, setTick] = useState(0)

  useEffect(() => {
    let active = true
    setLoading(true)
    setError('')
    api
      .get<T>(url)
      .then((res) => active && setData(res.data))
      .catch((err) => active && setError(apiErrorMessage(err)))
      .finally(() => active && setLoading(false))
    return () => {
      active = false
    }
  }, [url, tick])

  const reload = useCallback(() => setTick((t) => t + 1), [])
  return { data, loading, error, reload }
}

export const useSimulations = () => useGet<Simulation[]>('/simulations')
export const useSimulation = (id: string | number) => useGet<Simulation>(`/simulations/${id}`)
export const useProjects = () => useGet<Project[]>('/projects')
export const useProject = (id: string | number) => useGet<Project>(`/projects/${id}`)
export const useProjectShares = (id: string | number) =>
  useGet<ProjectShare[]>(`/projects/${id}/shares`)
